/* eslint-disable import/order */
import React, { useState, useMemo, useEffect, useRef } from "react"
import { ThemeProvider } from "styled-components"

import useGtm from "./hooks/useGtm"
import useScripts from "./hooks/useScripts"
import useStyles from "./hooks/useStyles"
import usePermutive from "./hooks/usePermutive"
import useFooter from "./hooks/useFooter"
import useAds from "./hooks/useAds"
import useBody from "./hooks/useBody"
import useCitations from "./citations/useCitations"
import usePromotions from "./hooks/usePromotions"
import useAdTargeting from "./hooks/useAdTargeting"
import useTrackBodyLinks from "./hooks/useTrackBodyLinks"
import usePageProgressTracker from "./hooks/usePageProgressTracker"
import ChartbeatScript from "./scripts/chartbeat"
import articleThemeExtension from "utils/articleThemeExtension"
import { FeaturedVideosProvider } from "store/contexts/featured-videos"
import Metadata from "./metadata"
import Schema from "./schema"
import Author from "./author"
import Top from "./top"
import Element from "./element"
import RightRail from "./right-rail"
import Citations from "./citations"
import LeaderboardAd from "./ads/leaderboard-ad"
import BottomStickyAd from "./ads/bottom-sticky-ad"
import {
  Container,
  ArticleContainer,
  MainContent,
  Elements,
  EmptyRailWrapper,
  LeftRailContent,
  EmptyRightRail,
  LeaderboardAdContainer,
  BottomStickyAdContainer,
  BetweenContentAdContainer,
} from "./styles"

import AstrologyLinks from "templates/horoscope/top"
import BottomStickyProduct from "./product/bottom-sticky-product"
import PremiumHeader from "./premium-header"
import BottomInlineAd from "./ads/bottom-inline-ad"
import Navigation from "components/article/navigation"

const Article = ({
  article,
  contentfulPromotionalPlacements,
  isPreview = false,
  isTop = false,
  loadedArticlesPromotion,
  handleLoadedArticlesPromotion = () => {},
  index,
  hasAdsOnly,
  isCurrentArticle,
  htlScriptLoaded,
}) => {
  const {
    dataLayer,
    contentfulAdUnits,
    bodyColor,
    navigationTransparency,
    category,
    tags,
    template,
    scripts,
    styles,
    recipeSchema,
  } = article

  const targetingOptions = useAdTargeting(article)
  const inlineAdIndex = useRef(1)
  const rightRailAdIndex = useRef(1)

  useGtm(dataLayer)
  useScripts(scripts)
  useStyles(styles)
  usePermutive(article)

  const theme = articleThemeExtension(category)

  const contentfulAd = {
    mobile: contentfulAdUnits?.find(
      (unit) => unit.device === "Mobile" && unit.layout === "Horizontal Rectangle",
    ),
    desktop: contentfulAdUnits?.find(
      (unit) => unit.device === "Desktop / Tablet" && unit.layout === "Horizontal Rectangle",
    ),
  }

  useBody(article, contentfulAd, inlineAdIndex)

  const { showAds } = useAds(article, isPreview, htlScriptLoaded)
  const citationList = useCitations(article, theme)

  usePromotions(
    article,
    contentfulAd,
    contentfulPromotionalPlacements,
    inlineAdIndex,
    !isTop,
    loadedArticlesPromotion,
    handleLoadedArticlesPromotion,
    hasAdsOnly,
  )

  usePageProgressTracker(article, index)
  const trackClickedLink = useTrackBodyLinks()

  const isFeatured = template === "feature"
  const isPremium = template === "premium"
  const isClean = template === "clean"
  const isStandard = !isFeatured && !isPremium && !isClean
  const premiumHeaderElement = isPremium && article.elements[0]

  const bodyBackground = isPremium && bodyColor ? bodyColor : "#fff"
  const isTranslucent = isPremium && navigationTransparency !== "solid"

  useEffect(() => {
    if (isPremium) {
      document.body.style.background = bodyBackground
    }
  }, [isPremium, bodyBackground])

  const { bottomStickyProduct, setBottomStickyProduct } = useFooter(
    article,
    isPremium,
    bodyBackground,
  )

  const articleElementsLength = article?.elements?.length
  return (
    <ThemeProvider theme={theme}>
      <FeaturedVideosProvider>
        <Metadata article={article} />
        <Schema article={article} recipeSchema={recipeSchema} />
        <ChartbeatScript article={article} />
        {isPreview && (
          <Navigation
            id="article-navigation"
            article={article}
            bodyBackground={bodyBackground}
            isTranslucent={isTranslucent}
          />
        )}
        <Container>
          {index === 0 && tags.includes("astrology") && <AstrologyLinks isArticle />}

          {index === 0 && (
            <LeaderboardAdContainer id="article-leaderboard-ad">
              <LeaderboardAd article={article} targetingOptions={targetingOptions} />
            </LeaderboardAdContainer>
          )}

          <ArticleContainer isFeatured={isFeatured} isPremium={isPremium} isClean={isClean}>
            <span id={`article-container-${article.id}`}>
              {isCurrentArticle && (
                <BottomStickyAdContainer id={`article-bottom-sticky-ad-${article.id}`}>
                  <BottomStickyAd article={article} />
                </BottomStickyAdContainer>
              )}
              {bottomStickyProduct && (
                <BottomStickyProduct
                  element={bottomStickyProduct}
                  isCurrentArticle={isCurrentArticle}
                />
              )}

              <MainContent isPremium={isPremium}>
                <Elements
                  background={bodyBackground}
                  topMargin={isPremium}
                  onClick={(event) => trackClickedLink(event.target)}
                >
                  {!isPremium && !isClean ? (
                    <Top article={article} />
                  ) : (
                    premiumHeaderElement && (
                      <PremiumHeader
                        article={article}
                        element={premiumHeaderElement}
                        background={bodyBackground}
                        isTranslucent={isTranslucent}
                      />
                    )
                  )}
                  {article?.elements?.map((element, idx) => (
                    <span key={`element-${element.uuid}`} {...(idx === 1 && { id: "content" })}>
                      <Element
                        article={article}
                        articleIndex={index}
                        contentfulAdUnits={contentfulAdUnits}
                        element={element}
                        showAds={showAds}
                        background={bodyBackground}
                        setBottomStickyProduct={setBottomStickyProduct}
                        index={idx}
                        elementsLength={article?.elements?.length}
                        inlineAdIndex={inlineAdIndex}
                        articleElementsLength={articleElementsLength}
                      />
                    </span>
                  ))}
                </Elements>
                {isStandard && (
                  <RightRail
                    article={article}
                    contentfulAdUnits={contentfulAdUnits}
                    isPreview={isPreview}
                    showAds={showAds}
                    adIndex={rightRailAdIndex}
                    index={index}
                  />
                )}
              </MainContent>

              <EmptyRailWrapper background={bodyBackground}>
                <LeftRailContent>
                  <Author article={article} />
                  {citationList.length > 0 && <Citations items={citationList} />}
                </LeftRailContent>
                {isStandard && <EmptyRightRail />}
              </EmptyRailWrapper>
              {isStandard && (
                <BetweenContentAdContainer id="article-between-content-ad">
                  <BottomInlineAd index={index} nsfw={article.nsfw} articleId={article.id} />
                </BetweenContentAdContainer>
              )}
            </span>
          </ArticleContainer>
        </Container>
      </FeaturedVideosProvider>
    </ThemeProvider>
  )
}

export default Article
